import { Avatar, Tooltip, Typography } from '@material-ui/core';
import { AccountBalance, Help } from '@material-ui/icons';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { BankErrorBanner } from 'shared/errors/SuperPayBankErrors';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import {
  BankAccountVerificationStatusType,
  SuperPayBankAccountDTO,
} from 'shared/modules/superpay/SuperPayDTO';
import { LinkButton } from 'shared/routing/Links';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import { BankValidationErrorBanner } from './BankErrorBanners';
import { BankVerification } from './BankVerification';
import { BankVerificationStatusTag } from './BankVerificationStatusTag';
import { PaymentsInMotionAlert } from './PaymentsInMotionAlert';
import { SuperPayACHBankDifferentBanner } from './SuperPayACHBankDifferentBanner';

const BankAvatar = styled(Avatar)`
  width: 56px;
  height: 56px;
  background: ${ColorDynamic.Silver200};
`;

interface ChangeBankAccountActionProps {
  verificationStatus?: BankAccountVerificationStatusType;
  hoursUntilUpdateAllowed?: number | null;
  path: string;
  fullWidth?: boolean;
}

function ChangeBankAccountAction({
  verificationStatus,
  hoursUntilUpdateAllowed,
  path,
  fullWidth,
}: ChangeBankAccountActionProps) {
  if (verificationStatus !== 'verified' && verificationStatus !== 'errored') {
    return (
      <Tooltip
        title="A bank account change is already in progress. You can start a new one once it's complete."
        arrow={true}
      >
        <Box>
          <Button variant="neutral" fullWidth={fullWidth} disabled={true}>
            Change Bank Account
          </Button>
        </Box>
      </Tooltip>
    );
  }

  const isUpdateBlockedByContactChange = !!(
    hoursUntilUpdateAllowed && hoursUntilUpdateAllowed > 0
  );
  const contactChangeTooltipTitle = isUpdateBlockedByContactChange
    ? `You have changed your email/phone in the last 24 hours. You can't change SuperPay bank account for ${hoursUntilUpdateAllowed} ${formatPlural(hoursUntilUpdateAllowed, 'hour', 'hours')}.`
    : '';

  return (
    <PermissionAction name="superpay" permission="can_edit">
      <Tooltip
        title={contactChangeTooltipTitle}
        disableHoverListener={!isUpdateBlockedByContactChange}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        arrow={true}
      >
        <Box>
          <LinkButton
            variant="neutral"
            fullWidth={fullWidth}
            to={`${path}/change-bank-account?utm_medium=SuperPay Settings`}
            disabled={isUpdateBlockedByContactChange}
          >
            Change Bank Account
          </LinkButton>
        </Box>
      </Tooltip>
    </PermissionAction>
  );
}

function HowSuperPayWorksHelpLink() {
  return (
    <Inline verticalAlign="center" space="xxsmall">
      <Box fontSize="16px">
        <Help fontSize="inherit" color="action" />
      </Box>
      <Box fontSize="14px">
        <WebViewLink
          target="_blank"
          rel="noopener noreferrer"
          href="https://support.superdispatch.com/hc/en-us/categories/35595057094163-SuperPay"
        >
          How SuperPay works
        </WebViewLink>
      </Box>
    </Inline>
  );
}

interface BankDetailsProps {
  bankAccount?: SuperPayBankAccountDTO;
  previousBankAccount?: SuperPayBankAccountDTO;
  hoursUntilUpdateAllowed?: number | null;
  hasAccountFeature?: boolean;
  path: string;
}

export function BankDetails({
  bankAccount,
  previousBankAccount,
  hoursUntilUpdateAllowed,
  hasAccountFeature,
  path,
}: BankDetailsProps) {
  const isScreenshotUploadEnabled = useFlag(
    'payments_superpay_micro_deposit_screenshot',
  );

  if (!bankAccount) {
    return (
      <Stack>
        <Typography variant="h3">Bank Account</Typography>
        <Typography color="textSecondary">No data</Typography>
      </Stack>
    );
  }

  const bankName = bankAccount.bank_name;
  const bankAccountDisplayNumber = bankAccount.display_number;
  const verificationStatus = bankAccount.verification_status;
  const shouldShowVerification =
    verificationStatus === 'new' || verificationStatus === 'pending';

  const hasValidationError =
    verificationStatus === 'errored' ||
    verificationStatus === 'failed' ||
    verificationStatus === 'locked';

  return (
    <Stack space="large">
      {shouldShowVerification && (
        <Box margin="auto" maxWidth="500px">
          <BankVerification
            path={path}
            bankName={bankName}
            hasAccountFeature={hasAccountFeature}
            bankAccountDisplayNumber={bankAccountDisplayNumber}
            bankAccountDateSent={bankAccount.microdeposit_sent_at}
            bankAccountAvailableDate={bankAccount.verification_available_date}
            isVerificationAvailable={bankAccount.is_verification_available}
          />
        </Box>
      )}

      <Stack space="medium">
        <Box aria-label="bank account details" margin="auto" maxWidth="496px">
          <Stack space="small">
            <Columns
              reverse={[true, false]}
              align={['top', 'center']}
              collapseBelow="tablet"
              space={['large', 'small']}
            >
              <Column>
                <Typography variant="h3">Bank Account</Typography>
              </Column>

              <Column width="content">
                {!isWebView ? (
                  <ChangeBankAccountAction
                    verificationStatus={verificationStatus}
                    hoursUntilUpdateAllowed={hoursUntilUpdateAllowed}
                    path={path}
                  />
                ) : (
                  <HowSuperPayWorksHelpLink />
                )}
              </Column>
            </Columns>

            <Columns
              collapseBelow="tablet"
              space="small"
              align={['top', 'center']}
            >
              <Column>
                <Inline verticalAlign="center" space="small">
                  <BankAvatar>
                    <AccountBalance color="action" />
                  </BankAvatar>

                  <Stack space="none">
                    <Typography variant="h3">
                      &bull;&bull;&bull;&bull;{bankAccountDisplayNumber}
                    </Typography>

                    <Typography color="textSecondary">{bankName}</Typography>
                  </Stack>
                </Inline>
              </Column>

              <Column width="content">
                {verificationStatus && (
                  <BankVerificationStatusTag status={verificationStatus} />
                )}
              </Column>
            </Columns>
            {isScreenshotUploadEnabled &&
              verificationStatus === 'in_review' && (
                <ScreenshotInReviewBanner
                  bankAccountDisplayNumber={bankAccountDisplayNumber}
                />
              )}
          </Stack>
        </Box>

        {isWebView && (
          <Box margin="auto" maxWidth="496px">
            <ChangeBankAccountAction
              verificationStatus={verificationStatus}
              hoursUntilUpdateAllowed={hoursUntilUpdateAllowed}
              path={path}
              fullWidth={true}
            />
          </Box>
        )}

        {bankAccount.payment_error && (
          <BankErrorBanner
            paymentError={bankAccount.payment_error}
            source="SuperPay Settings"
          />
        )}
        {!bankAccount.payment_error && hasValidationError && (
          <BankValidationErrorBanner verificationStatus={verificationStatus} />
        )}

        {previousBankAccount && (
          <PaymentsInMotionAlert bankAccount={previousBankAccount} />
        )}

        <SuperPayACHBankDifferentBanner bankAccount={bankAccount} />
      </Stack>
    </Stack>
  );
}

const InReviewText = styled(Typography)`
  color: ${Color.Yellow300};
`;

function ScreenshotInReviewBanner({
  bankAccountDisplayNumber,
}: {
  bankAccountDisplayNumber?: string | null;
}) {
  return (
    <Box padding="small" borderRadius="small" backgroundColor="Yellow50">
      <InReviewText>
        We’re reviewing the screenshot you submitted
        {bankAccountDisplayNumber && (
          <>
            {' '}
            for <b>&bull;&bull;&bull;&bull;{bankAccountDisplayNumber}</b>
          </>
        )}
        .<br />
        This usually takes 1 business day.
      </InReviewText>
    </Box>
  );
}
